import { useState } from "react";
import type { ComponentType } from "react";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  MessageSquare,
  Settings,
  PlusCircle,
  Trash2,
  BarChart2,
  LayoutDashboard,
  Activity,
  Bell,
  CalendarClock,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Search,
} from "lucide-react";
import type { LucideProps } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { useConversations } from "../../hooks";
import type { Conversation } from "../../hooks/useConversations";
import { isAdmin, hasRole, ANALYST_ROLES } from "../../constants/roles";
import { timeAgo } from "../../utils";

type IconType = ComponentType<LucideProps>;

type SidebarProps = {
  open: boolean;
  onClose: () => void;
};

const MY_BRIAN_ITEMS: { to: string; icon: IconType; label: string }[] = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard Builder" },
];

const CHATS_PREVIEW = 5;
const MODAL_PAGE_SIZE = 10;

type SortKey = "title" | "updated_at" | "created_at";
type SortDir = "asc" | "desc";

function SortIcon({ col, sortKey, sortDir }: { col: SortKey; sortKey: SortKey; sortDir: SortDir }) {
  if (sortKey !== col) return <ChevronUp size={11} className="opacity-20" />;
  return sortDir === "asc"
    ? <ChevronUp size={11} className="opacity-70" />
    : <ChevronDown size={11} className="opacity-70" />;
}

function AllChatsModal({
  conversations,
  activeConvId,
  onSelect,
  onClose,
  onRequestDelete,
}: {
  conversations: Conversation[];
  activeConvId: string | null;
  onSelect: (id: string) => void;
  onClose: () => void;
  onRequestDelete: (id: string) => void;
}) {
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey>("updated_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [search, setSearch] = useState("");

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(1);
  }

  const filtered = search.trim()
    ? conversations.filter((c) =>
        (c.title ?? "").toLowerCase().includes(search.trim().toLowerCase())
      )
    : conversations;

  const sorted = [...filtered].sort((a, b) => {
    let cmp: number;
    if (sortKey === "title") {
      cmp = (a.title ?? "").localeCompare(b.title ?? "");
    } else if (sortKey === "created_at") {
      cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    } else {
      cmp = new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
    }
    return sortDir === "asc" ? cmp : -cmp;
  });

  const totalPages = Math.ceil(sorted.length / MODAL_PAGE_SIZE);
  const pageItems = sorted.slice((page - 1) * MODAL_PAGE_SIZE, page * MODAL_PAGE_SIZE);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 flex flex-col w-full max-w-2xl rounded-xl border border-gray-200 bg-white shadow-xl max-h-[85vh]">
        {/* Header */}
        <div className="px-5 pt-4 pb-3 border-b border-gray-100 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">All chats</h2>
            <button onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100">
              <X size={16} />
            </button>
          </div>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search chats…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full rounded-lg border border-gray-200 py-1.5 pl-7 pr-3 text-xs text-gray-800 placeholder-gray-400 focus:border-blue-400 focus:outline-none"
            />
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto p-4">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-2.5 text-left">
                  <button
                    onClick={() => toggleSort("title")}
                    className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600"
                  >
                    Title <SortIcon col="title" sortKey={sortKey} sortDir={sortDir} />
                  </button>
                </th>
                <th className="px-4 py-2.5 text-left w-28">
                  <button
                    onClick={() => toggleSort("updated_at")}
                    className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600"
                  >
                    Last updated <SortIcon col="updated_at" sortKey={sortKey} sortDir={sortDir} />
                  </button>
                </th>
                <th className="px-4 py-2.5 text-left w-28">
                  <button
                    onClick={() => toggleSort("created_at")}
                    className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600"
                  >
                    Created <SortIcon col="created_at" sortKey={sortKey} sortDir={sortDir} />
                  </button>
                </th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {pageItems.map((conv) => (
                <tr
                  key={conv.id}
                  onClick={() => onSelect(conv.id)}
                  className={`group cursor-pointer hover:bg-gray-50 ${activeConvId === conv.id ? "bg-blue-50" : ""}`}
                >
                  <td className="px-4 py-2.5">
                    <span className={`truncate block max-w-xs text-sm ${activeConvId === conv.id ? "text-[#1D345F] font-medium" : "text-gray-800"}`}>
                      {conv.title || "Untitled"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-400 whitespace-nowrap">
                    {timeAgo(conv.updated_at ?? conv.created_at) ?? "—"}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-400 whitespace-nowrap">
                    {timeAgo(conv.created_at) ?? "—"}
                  </td>
                  <td className="pr-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); onRequestDelete(conv.id); }}
                      className="hidden group-hover:block rounded p-0.5 text-gray-300 hover:text-red-500"
                      title="Delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3">
            <span className="text-xs text-gray-400">
              {sorted.length} {search.trim() ? "results" : "chats"} · page {page} of {totalPages}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="rounded px-2.5 py-1 text-xs text-gray-500 hover:bg-gray-100 disabled:opacity-30"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="rounded px-2.5 py-1 text-xs text-gray-500 hover:bg-gray-100 disabled:opacity-30"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Sidebar({ open, onClose }: SidebarProps) {
  const { user } = useAuth();
  const agentName = user?.agent_name || "Brian";
  const userIsAdmin = isAdmin(user);
  const perms = user?.permissions;
  const can = (feature: string) =>
    user?.role?.name === "super_admin" || !perms || perms[feature] !== false;

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const activeConvId = searchParams.get("c");

  const isAdminRoute = location.pathname.startsWith("/admin");
  const [expandOverride, setExpandOverride] = useState(false);
  const collapsed = isAdminRoute && !expandOverride;

  const { conversations, removeConversation } = useConversations();
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const previewConvs = conversations.slice(0, CHATS_PREVIEW);
  const hasMore = conversations.length > CHATS_PREVIEW;

  function newChat() {
    navigate("/chat");
    onClose();
  }

  function deleteConv(e: React.MouseEvent, id: string) {
    e.preventDefault();
    e.stopPropagation();
    setConfirmDeleteId(id);
  }

  function confirmAndDelete() {
    if (!confirmDeleteId) return;
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    removeConversation(id)
      .then(() => {
        if (activeConvId === id) navigate("/chat");
      })
      .catch(() => {});
  }

  function selectConv(id: string) {
    setModalOpen(false);
    navigate(`/chat?c=${id}`);
    onClose();
  }

  return (
    <>
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 flex h-screen w-64 flex-col border-r border-gray-200 bg-sidebar
          transform transition-transform duration-200 ease-in-out
          ${open ? "translate-x-0" : "-translate-x-full"}
          md:relative md:translate-x-0 md:transition-none ${collapsed ? "md:w-16" : "md:w-56"}
        `}
      >
        {isAdminRoute && (
          <button
            onClick={() => setExpandOverride((o) => !o)}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="absolute top-6 -right-3 z-10 hidden h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm hover:border-gray-300 hover:text-[#1D345F] md:flex"
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        )}
        <div className={`flex items-center pt-4 pb-0 ${collapsed ? "justify-center px-2" : "justify-between px-4"}`}>
          <img
            src="/logo.png"
            alt="Logo"
            className={`brightness-0 invert ${collapsed ? "h-auto w-9" : "h-auto w-2/5"}`}
          />
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-white hover:bg-white/10 md:hidden"
            title="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        <div className={`flex items-center py-4 ${collapsed ? "justify-center px-2" : "justify-between px-4"}`}>
          {!collapsed && <span className="text-base font-semibold text-white">{agentName}</span>}
          {can("chat") && (
            <button
              onClick={newChat}
              title="New chat"
              className="rounded-lg p-1 text-white hover:bg-gray-100 hover:text-[#1D345F]"
            >
              <PlusCircle size={18} />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-2">
          {/* My Brian — always at top */}
          <div className="mb-4">
            {!collapsed && (
              <div className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-white/60">
                My Brian
              </div>
            )}
            <div className="space-y-0.5">
              {MY_BRIAN_ITEMS.map((item) => (
                <SidebarNavLink
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  onClick={onClose}
                  collapsed={collapsed}
                />
              ))}
              {hasRole(user, ANALYST_ROLES) && (
                <SidebarNavLink to="/custom-alerts" icon={Bell} label="Custom Alerts" onClick={onClose} collapsed={collapsed} />
              )}
            </div>
          </div>

          {/* Chats — collapsed: single icon that opens the all-chats modal */}
          {can("chat") && collapsed && (
            <div className="mb-4 flex justify-center border-t border-gray-100 pt-3">
              <button
                onClick={() => setModalOpen(true)}
                title="Chats"
                className="rounded-lg p-2 text-white hover:bg-gray-100 hover:text-[#1D345F]"
              >
                <MessageSquare size={16} />
              </button>
            </div>
          )}

          {/* Chats */}
          {can("chat") && !collapsed && conversations.length > 0 && (
            <div className="mb-4 border-t border-gray-100 pt-3">
              <div className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-white">
                Chats
              </div>
              <nav className="space-y-0.5">
                {previewConvs.map((conv) => (
                  <div key={conv.id} className="group relative">
                    <NavLink
                      to={`/chat?c=${conv.id}`}
                      onClick={onClose}
                      className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs transition-colors ${
                        activeConvId === conv.id
                          ? "bg-blue-50 text-[#1D345F]"
                          : "text-white hover:bg-gray-100 hover:text-[#1D345F]"
                      }`}
                    >
                      <MessageSquare size={13} className="shrink-0" />
                      <span className="truncate">{conv.title}</span>
                    </NavLink>
                    <button
                      onClick={(e) => deleteConv(e, conv.id)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 hidden rounded p-0.5 text-gray-400 hover:text-red-500 group-hover:block"
                      title="Delete"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </nav>
              {hasMore && (
                <button
                  onClick={() => setModalOpen(true)}
                  className="mt-1 flex w-full items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs text-white/60 hover:bg-gray-100 hover:text-[#1D345F] transition-colors"
                >
                  <ChevronRight size={12} />
                  More chats
                </button>
              )}
            </div>
          )}

          {/* Nav links */}
          <div className="space-y-0.5 border-t border-gray-100 pt-3">
            {can("reports_view") && (
              <SidebarNavLink to="/reports" icon={BarChart2} label="Reports" onClick={onClose} collapsed={collapsed} />
            )}
            {can("service_status_view") && (
              <SidebarNavLink to="/status" icon={Activity} label="Platform Health" onClick={onClose} collapsed={collapsed} />
            )}
            <SidebarNavLink to="/scheduled-actions" icon={CalendarClock} label="Scheduled Actions" onClick={onClose} collapsed={collapsed} />
            {userIsAdmin && (
              <SidebarNavLink to="/admin" icon={Settings} label="Admin" onClick={onClose} collapsed={collapsed} />
            )}
          </div>
        </div>

        <div className={`border-t border-gray-200 ${collapsed ? "p-2" : "p-4"}`}>
          <button
            onClick={() => { navigate("/settings"); onClose(); }}
            title={collapsed ? user?.email : undefined}
            className={`flex w-full items-center rounded-lg text-white hover:bg-gray-100 hover:text-[#1D345F] transition-colors group ${
              collapsed ? "justify-center p-2" : "gap-2 px-3 py-2"
            }`}
          >
            {!collapsed && <span className="flex-1 truncate text-left text-xs">{user?.email}</span>}
            <Settings size={15} className="shrink-0 opacity-60 group-hover:opacity-100" />
          </button>
        </div>
      </aside>

      {modalOpen && (
        <AllChatsModal
          conversations={conversations}
          activeConvId={activeConvId}
          onSelect={selectConv}
          onRequestDelete={setConfirmDeleteId}
          onClose={() => setModalOpen(false)}
        />
      )}

      {confirmDeleteId && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setConfirmDeleteId(null)} />
          <div className="relative z-10 w-full max-w-sm rounded-xl border border-gray-200 bg-white p-6 shadow-xl">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Delete chat?</h3>
            <p className="text-sm text-gray-500 mb-5">This conversation will be permanently deleted and cannot be recovered.</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmAndDelete}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SidebarNavLink({
  to,
  icon: Icon,
  label,
  onClick,
  collapsed,
}: {
  to: string;
  icon: IconType;
  label: string;
  onClick?: () => void;
  collapsed?: boolean;
}) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        `flex items-center rounded-lg text-sm font-medium transition-colors ${
          collapsed ? "justify-center px-2 py-2" : "gap-3 px-3 py-2"
        } ${
          isActive ? "bg-blue-50 text-[#1D345F]" : "text-white hover:bg-gray-100 hover:text-[#1D345F]"
        }`
      }
    >
      <Icon size={16} />
      {!collapsed && label}
    </NavLink>
  );
}
